import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'

function HitLists() {
  const { user } = useAuth()
  const [lists, setLists] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchPublicLists()
  }, [])

  const fetchPublicLists = async () => {
    try {
      const { data, error } = await supabase
        .from('hitlists')
        .select(`
          hitlist_id, title, description, is_open_contributions, created_at, user_id,
          hitlist_items (
            item_id,
            lure_variants (
              base_lures (
                lure_reviews (rating)
              )
            )
          )
        `)
        .eq('is_public', true)
        .order('created_at', { ascending: false })

      if (error) throw error

      const userIds = [...new Set((data || []).map(l => l.user_id))]
      const { data: users } = userIds.length > 0
        ? await supabase.from('users').select('id, username').in('id', userIds)
        : { data: [] }

      const userMap = {}
      users?.forEach(u => { userMap[u.id] = u.username })

      setLists((data || []).map(list => {
        const allRatings = []
        list.hitlist_items?.forEach(item => {
          const reviews = item.lure_variants?.base_lures?.lure_reviews || []
          reviews.forEach(r => { if (r.rating != null) allRatings.push(r.rating) })
        })
        const avgRating = allRatings.length > 0
          ? allRatings.reduce((sum, r) => sum + r, 0) / allRatings.length
          : null

        return {
          ...list,
          ownerUsername: userMap[list.user_id] || 'Unknown',
          itemCount: list.hitlist_items?.length || 0,
          avgRating,
          ratingCount: allRatings.length
        }
      }))
      setLoading(false)
    } catch (err) {
      console.error('Error fetching hitlists:', err)
      setLoading(false)
    }
  }

  if (loading) return <div className="loading">Loading HitLists...</div>

  return (
    <div className="hitlists-page">
      <div className="page-header">
        <div>
          <h1>HitLists</h1>
          <p className="page-subtitle">Community-curated lure lists — organized by lake, season, color, and more</p>
        </div>
        {user ? (
          <Link to="/my-hitlists" className="btn-primary">+ Create a HitList</Link>
        ) : (
          <Link to="/login" className="btn-primary">Login to Create a HitList</Link>
        )}
      </div>

      {lists.length === 0 ? (
        <div className="no-content-box">
          <p>No public HitLists yet. Be the first to create one!</p>
          {user && (
            <Link to="/my-hitlists" className="btn-primary" style={{ marginTop: '1rem', display: 'inline-block' }}>
              Create a HitList
            </Link>
          )}
        </div>
      ) : (
        <div className="hitlists-grid">
          {lists.map(list => (
            <Link key={list.hitlist_id} to={`/hitlists/${list.hitlist_id}`} className="hitlist-card">
              <div className="hitlist-card-header">
                <h3>{list.title}</h3>
                {list.is_open_contributions && (
                  <span className="badge-open-contrib">👥 Open</span>
                )}
              </div>
              {list.description && (
                <p className="hitlist-description">{list.description}</p>
              )}
              <div className="hitlist-card-footer">
                <span className="hitlist-owner">by {list.ownerUsername}</span>
                <span className="hitlist-count">{list.itemCount} {list.itemCount === 1 ? 'lure' : 'lures'}</span>
                {list.avgRating !== null && (
                  <span className="hitlist-avg-rating" title={`${list.ratingCount} total ${list.ratingCount === 1 ? 'review' : 'reviews'}`}>
                    ★ {list.avgRating.toFixed(1)}
                  </span>
                )}
                <span className="hitlist-date">{new Date(list.created_at).toLocaleDateString()}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export default HitLists
